<template>
    <login-register-box>
        <template v-slot:formBox>
            <div class="login-title">
                <div class="login-title-en">SIGN-IN</div>
                <div class="login-title-cn">登录</div>
            </div>
            <div class="login-register-nav">
                <span>还没有注册？</span>
                <router-link to="/register">立即注册！</router-link>
            </div>
            <div class="login-form-box">
                <div class="login-mode-switch" role="tablist" aria-label="登录方式">
                    <el-button
                        :type="loginMode === 'sms' ? 'warning' : 'text'"
                        @click="switchLoginMode('sms')">
                        短信验证码登录
                    </el-button>
                    <el-button
                        :type="loginMode === 'password' ? 'warning' : 'text'"
                        @click="switchLoginMode('password')">
                        密码登录
                    </el-button>
                </div>
                <el-form
                    label-position="top"
                    :model="form"
                    :rules="rules"
                    ref="loginForm">
                    <el-form-item label="手机号 / Phone Number" prop="phone">
                        <el-input v-model.trim="form.phone" autocomplete="tel" />
                    </el-form-item>
                    <el-form-item
                        v-if="loginMode === 'sms'"
                        label="短信验证码 / Verification Code"
                        prop="smsCode">
                        <el-input
                            v-model.trim="form.smsCode"
                            maxlength="6"
                            autocomplete="one-time-code"
                            @keyup.enter.native="handleLogin">
                            <el-button
                                slot="suffix"
                                type="info"
                                size="mini"
                                :disabled="loginCodeCountdown > 0"
                                @click="handleSendLoginCode"
                                plain>
                                {{ loginCodeCountdown > 0 ? `${loginCodeCountdown}s 后重试` : '获取验证码' }}
                            </el-button>
                        </el-input>
                    </el-form-item>
                    <el-form-item
                        v-else
                        label="密码 / Password"
                        prop="password">
                        <el-input
                            v-model="form.password"
                            type="password"
                            autocomplete="current-password"
                            @keyup.enter.native="handleLogin" />
                    </el-form-item>
                    <el-form-item prop="accepted" class="login-consent-item">
                        <el-checkbox v-model="form.accepted">
                            我已阅读并同意《用户协议》和《隐私政策》
                        </el-checkbox>
                    </el-form-item>
                    <el-form-item class="login-actions-item">
                        <el-checkbox v-model="form.autoLogin">自动登录</el-checkbox>
                        <div class="login-form-forget">
                            <el-button type="text" @click="handleDialog(true)">忘记密码？</el-button>
                        </div>
                        <el-button type="warning" @click="handleLogin" class="login-form-submit">
                            立即登录
                        </el-button>
                    </el-form-item>
                </el-form>
            </div>
            <el-dialog
                :visible="dialogVisible"
                title="找回密码"
                width="30%"
                @close="handleDialog(false)"
                :close-on-click-modal="false"
                center>
                <el-form
                    label-position="top"
                    :model="forgetForm"
                    :rules="forgetRules"
                    ref="forgetForm">
                    <el-form-item label="手机号 / Phone Number" prop="phone">
                        <el-input v-model.trim="forgetForm.phone" autocomplete="tel" />
                    </el-form-item>
                    <el-form-item label="短信验证码 / Verification Code" prop="smsCode">
                        <el-input v-model.trim="forgetForm.smsCode" maxlength="6">
                            <el-button
                                slot="suffix"
                                type="info"
                                size="mini"
                                :disabled="resetCodeCountdown > 0"
                                @click="handleSendResetCode"
                                plain>
                                {{ resetCodeCountdown > 0 ? `${resetCodeCountdown}s 后重试` : '获取验证码' }}
                            </el-button>
                        </el-input>
                    </el-form-item>
                    <el-form-item label="新密码 / New Password" prop="newPassword">
                        <el-input
                            v-model="forgetForm.newPassword"
                            type="password"
                            maxlength="64"
                            autocomplete="new-password"
                            @keyup.enter.native="handleForgetPassword" />
                    </el-form-item>
                </el-form>
                <template #footer>
                    <el-button type="primary" @click="handleForgetPassword">确定</el-button>
                    <el-button @click="handleDialog(false)">关闭</el-button>
                </template>
            </el-dialog>
        </template>
    </login-register-box>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";
import { isValidPassword, PASSWORD_VALIDATION_MESSAGE } from "@/tool/password";

const validatePhone = (rule, value, callback) => {
    if (!/^1[3-9]\d{9}$/.test(value || "")) {
        callback(new Error("请输入正确的中国大陆手机号"));
        return;
    }
    callback();
};

const validateSmsCode = (rule, value, callback) => {
    if (!/^\d{6}$/.test(value || "")) {
        callback(new Error("请输入短信验证码"));
        return;
    }
    callback();
};

const validateAccepted = (rule, value, callback) => {
    if (!value) {
        callback(new Error("请先同意用户协议和隐私政策"));
        return;
    }
    callback();
};

export default {
    data() {
        const validateLoginPassword = (rule, value, callback) => {
            if (typeof value !== "string" || value.trim() === "") {
                callback(new Error("请输入密码"));
                return;
            }
            callback();
        };
        const validatePassword = (rule, value, callback) => {
            if (!isValidPassword(value || "")) {
                callback(new Error(PASSWORD_VALIDATION_MESSAGE));
                return;
            }
            callback();
        };

        return {
            loginMode: "sms",
            form: {
                phone: "",
                password: "",
                smsCode: "",
                autoLogin: false,
                accepted: false,
            },
            forgetForm: {
                phone: "",
                smsCode: "",
                newPassword: "",
            },
            rules: {
                phone: [{ required: true, validator: validatePhone, trigger: "blur" }],
                password: [{ required: true, validator: validateLoginPassword, trigger: "blur" }],
                smsCode: [{ required: true, validator: validateSmsCode, trigger: "blur" }],
                accepted: [{ required: true, validator: validateAccepted, trigger: "change" }],
            },
            forgetRules: {
                phone: [{ required: true, validator: validatePhone, trigger: "blur" }],
                newPassword: [{ required: true, validator: validatePassword, trigger: "blur" }],
                smsCode: [{ required: true, validator: validateSmsCode, trigger: "blur" }],
            },
            loginCodeCountdown: 0,
            resetCodeCountdown: 0,
            loginCodeTimer: null,
            resetCodeTimer: null,
        };
    },
    computed: mapState({
        dialogVisible: (state) => state.dialogVisible,
    }),
    components: {
        LoginRegisterBox: () => import("@/components/LoginRegisterBox"),
    },
    methods: {
        ...mapMutations(["changeDialogVisible"]),
        ...mapActions(["sendLoginCode", "sendResetCode", "postLogin", "handleForget"]),
        startCountdown(countdownKey, timerKey) {
            this[countdownKey] = 60;
            clearInterval(this[timerKey]);
            this[timerKey] = setInterval(() => {
                this[countdownKey] -= 1;
                if (this[countdownKey] <= 0) {
                    clearInterval(this[timerKey]);
                    this[timerKey] = null;
                }
            }, 1000);
        },
        switchLoginMode(mode) {
            this.loginMode = mode;
            this.form.password = "";
            this.form.smsCode = "";
            this.$nextTick(() => this.$refs.loginForm.clearValidate());
        },
        handleSendLoginCode() {
            this.$refs.loginForm.validateField("phone", (errorMessage) => {
                if (errorMessage) return;
                this.sendLoginCode(this.form.phone).then(({ message, smsCode }) => {
                    this.$message.success(message);
                    if (smsCode) {
                        this.form.smsCode = smsCode;
                    }
                    this.startCountdown("loginCodeCountdown", "loginCodeTimer");
                }).catch((message) => this.$message.error(message));
            });
        },
        handleSendResetCode() {
            this.$refs.forgetForm.validateField("phone", (errorMessage) => {
                if (errorMessage) return;
                this.sendResetCode(this.forgetForm.phone).then(({ message, smsCode }) => {
                    this.$message.success(message);
                    if (smsCode) {
                        this.forgetForm.smsCode = smsCode;
                    }
                    this.startCountdown("resetCodeCountdown", "resetCodeTimer");
                }).catch((message) => this.$message.error(message));
            });
        },
        handleDialog(visible) {
            if (!visible) {
                clearInterval(this.resetCodeTimer);
                this.resetCodeTimer = null;
                this.resetCodeCountdown = 0;
                this.forgetForm = { phone: "", smsCode: "", newPassword: "" };
            }
            this.changeDialogVisible(visible);
        },
        handleForgetPassword() {
            this.$refs.forgetForm.validate((valid) => {
                if (!valid) return;
                this.handleForget(this.forgetForm).then((result) => {
                    this.$message({ message: result.msg, type: result.type ? "success" : "error" });
                    if (result.type) this.handleDialog(false);
                });
            });
        },
        handleLogin() {
            this.$refs.loginForm.validate((valid) => {
                if (!valid) return;
                const credential = this.loginMode === "sms"
                    ? { smsCode: this.form.smsCode }
                    : { password: this.form.password };
                this.postLogin({
                    phone: this.form.phone,
                    autoLogin: this.form.autoLogin,
                    agreementAccepted: this.form.accepted,
                    privacyAccepted: this.form.accepted,
                    ...credential,
                }).then((result) => {
                    this.$message({ message: result.msg, type: result.type });
                    if (result.type !== "success") return;
                    const redirect = this.$route.query.redirect;
                    const target = typeof redirect === "string" &&
                        redirect.indexOf("/") === 0 && redirect.indexOf("//") !== 0
                        ? redirect
                        : "/main";
                    setTimeout(() => this.$router.replace(target), 500);
                });
            });
        },
    },
    beforeDestroy() {
        clearInterval(this.loginCodeTimer);
        clearInterval(this.resetCodeTimer);
    },
};
</script>

<style scoped>
.login-title > div {
    font-size: 30px;
    text-align: center;
}

.login-title-en {
    font-weight: bold;
}

.login-register-nav {
    margin: 30px 0 10px;
}

.login-form-box {
    margin-bottom: 10px;
    padding: 20px;
    border: 1px solid #EBEEF5;
    border-radius: 10px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, .1);
}

.login-mode-switch {
    display: flex;
    margin-bottom: 12px;
    border-bottom: 1px solid #EBEEF5;
}

.login-form-box >>> .el-form-item__label {
    margin: 0;
    padding: 0;
    color: #909399;
}

.login-consent-item {
    margin-bottom: 0;
}

.login-actions-item {
    margin-bottom: 0;
}

.login-form-forget {
    text-align: right;
}

.login-form-submit {
    width: 100%;
}
</style>
